import React, { useEffect, useMemo, useState } from "react";
import RateMeCommentBlock from "./RateMeCommentBlock";
import RateMeReplyBlock from "./RateMeReplyBlock";

function cx(...classes) {
  return classes.filter(Boolean).join(" ");
}

function isRateMe(comment) {
  return (
    comment?.kind === "rate_me" ||
    comment?.type === "rate_me" ||
    Boolean(comment?.audio?.audio_url)
  );
}

function isRateMeReply(comment) {
  return (
    comment?.kind === "rate_me_reply" ||
    comment?.type === "rate_me_reply" ||
    Boolean(comment?.ratings?.length || comment?.sentenceRating)
  );
}

function getCommentId(comment) {
  return comment?.id || comment?._id;
}

function getAuthorName(comment) {
  return comment?.username || comment?.user || comment?.author || "User";
}

function getSubjectAuthor(comment) {
  return comment?.subject?.author || comment?.subjectAuthor || "Unknown author";
}

function getSubjectLanguage(comment) {
  return comment?.subject?.language || comment?.language || "Unknown language";
}

function isAudioLikeFile(value) {
  return /\.(mp3|wav|m4a|webm|ogg|aac)$/i.test(String(value || ""));
}

function getChapterDisplay(comment) {
  if (comment?.chapterTitle) return comment.chapterTitle;

  if (comment?.file && !isAudioLikeFile(comment.file)) {
    return comment.file;
  }

  return "";
}

function getKindLabel(commentOrKind) {
  const kind =
    typeof commentOrKind === "string" ? commentOrKind : commentOrKind?.kind;

  if (kind === "rate_me" || isRateMe(commentOrKind)) return "Rate me";
  if (kind === "rate_me_reply" || isRateMeReply(commentOrKind)) return "Verdict";
  return "Discussion";
}

function getKindStyle(comment) {
  if (isRateMe(comment)) {
    return "border-violet-200 bg-violet-50 text-violet-700";
  }

  if (isRateMeReply(comment)) {
    return "border-green-200 bg-green-50 text-green-700";
  }

  return "border-blue-200 bg-blue-50 text-blue-700";
}

function formatDate(rawDate) {
  if (!rawDate) return "";

  const date = rawDate instanceof Date ? rawDate : new Date(rawDate);

  if (Number.isNaN(date.getTime())) return "";

  return date.toLocaleString([], {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function getSnippet(comment) {
  const text =
    comment?.content ||
    comment?.text ||
    comment?.comment ||
    comment?.anchor?.text ||
    "";

  const cleaned = String(text).trim();

  if (!cleaned || cleaned === "Human pronunciation verdict") {
    if (isRateMe(comment)) return "Asked for a human pronunciation assessment.";
    if (isRateMeReply(comment)) return "Submitted a human pronunciation verdict.";
    return "No note added.";
  }

  return cleaned.length > 130 ? `${cleaned.slice(0, 130)}…` : cleaned;
}

function getViewerId(auth) {
  return (
    auth?.id ||
    auth?._id ||
    auth?.userId ||
    auth?.user?.id ||
    auth?.user?._id ||
    auth?.user?.userId ||
    auth?.currentUser?.id ||
    auth?.currentUser?._id ||
    auth?.currentUser?.userId ||
    localStorage.getItem("userId") ||
    null
  );
}

function getViewerName(auth) {
  return (
    auth?.username ||
    auth?.user?.username ||
    auth?.currentUser?.username ||
    localStorage.getItem("username") ||
    null
  );
}

function canManageComment(comment, auth) {
  if (!comment || comment?.isDeleted) return false;

  const viewerId = getViewerId(auth);
  const viewerName = getViewerName(auth);
  const role = auth?.role || auth?.user?.role || auth?.currentUser?.role;

  if (role === "ADMIN") return true;

  if (viewerId && String(comment?.userId) === String(viewerId)) return true;
  if (viewerName && String(comment?.username) === String(viewerName)) return true;

  return false;
}

function makeSubjectLabel(subject) {
  if (!subject) return "";

  const title = subject.title || subject.subjectTitle || "Untitled";
  const author = subject.author || subject.subjectAuthor || "Unknown author";
  const language = subject.language || "Unknown language";

  return `${title} — ${author} · ${language}`;
}

function SelectFilter({
  label,
  value,
  onChange,
  options,
  allLabel = "All",
  disabled = false,
}) {
  return (
    <label className="flex min-w-0 flex-1 flex-col gap-1">
      <span className="text-[10px] font-black uppercase tracking-[0.16em] text-slate-400">
        {label}
      </span>

      <select
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        className="h-11 min-w-0 rounded-2xl border border-slate-200 bg-white px-3 text-sm font-bold text-slate-700 shadow-sm outline-none transition focus:border-blue-300 focus:ring-4 focus:ring-blue-100 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400"
      >
        <option value="all">{allLabel}</option>

        {(options || []).map((option) => {
          const normalized =
            typeof option === "string"
              ? { value: option, label: option }
              : option;

          return (
            <option key={normalized.value} value={normalized.value}>
              {normalized.label}
            </option>
          );
        })}
      </select>
    </label>
  );
}

function Pagination({ page, totalPages, setPage, compact = false }) {
  if (totalPages <= 1) return null;

  return (
    <div
      className={cx(
        "flex flex-wrap items-center justify-center gap-3",
        compact ? "mt-5" : "mt-8"
      )}
    >
      <button
        type="button"
        disabled={page <= 1}
        onClick={() => setPage((p) => Math.max(1, p - 1))}
        className={cx(
          "rounded-2xl border px-5 py-2.5 text-sm font-black shadow-sm transition",
          page <= 1
            ? "cursor-not-allowed border-slate-100 bg-slate-100 text-slate-300"
            : "border-slate-200 bg-white text-slate-700 hover:-translate-y-0.5 hover:bg-slate-50"
        )}
      >
        Previous
      </button>

      <span className="rounded-full bg-white px-4 py-2 text-sm font-black text-slate-500 shadow-sm">
        {page} / {totalPages}
      </span>

      <button
        type="button"
        disabled={page >= totalPages}
        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
        className={cx(
          "rounded-2xl border px-5 py-2.5 text-sm font-black shadow-sm transition",
          page >= totalPages
            ? "cursor-not-allowed border-slate-100 bg-slate-100 text-slate-300"
            : "border-slate-200 bg-white text-slate-700 hover:-translate-y-0.5 hover:bg-slate-50"
        )}
      >
        Next
      </button>
    </div>
  );
}

function Pill({ children, className, onRemove }) {
  if (!children) return null;

  if (onRemove) {
    return (
      <button
        type="button"
        onClick={onRemove}
        className={cx(
          "inline-flex max-w-full items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-black transition hover:-translate-y-0.5",
          className || "border-slate-200 bg-slate-50 text-slate-500"
        )}
      >
        <span className="truncate">{children}</span>
        <span className="shrink-0 opacity-60">×</span>
      </button>
    );
  }

  return (
    <span
      className={cx(
        "inline-flex max-w-full items-center rounded-full border px-2.5 py-1 text-[11px] font-black",
        className || "border-slate-200 bg-slate-50 text-slate-500"
      )}
    >
      <span className="truncate">{children}</span>
    </span>
  );
}

function MetricPill({ label, value, tone = "slate" }) {
  const tones = {
    slate: "border-slate-200 bg-white text-slate-600",
    blue: "border-blue-200 bg-blue-50 text-blue-700",
    violet: "border-violet-200 bg-violet-50 text-violet-700",
    green: "border-green-200 bg-green-50 text-green-700",
  };

  return (
    <span
      className={cx(
        "inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-black",
        tones[tone] || tones.slate
      )}
    >
      <span>{value}</span>
      <span className="font-bold opacity-75">{label}</span>
    </span>
  );
}

function Chevron({ open }) {
  return (
    <span
      className={cx(
        "inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-500 shadow-sm transition",
        open ? "rotate-90" : "rotate-0"
      )}
    >
      ›
    </span>
  );
}

function ThreadHeader({ comment }) {
  const name = getAuthorName(comment);

  return (
    <div className="flex min-w-0 flex-wrap items-start justify-between gap-3">
      <div className="min-w-0 flex-1">
        <div className="flex min-w-0 items-center gap-2">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-600 to-indigo-600 text-sm font-black text-white shadow-md shadow-blue-600/20">
            {String(name).charAt(0).toUpperCase()}
          </div>

          <div className="min-w-0">
            <h3 className="truncate text-lg font-black text-slate-950">
              {name}
            </h3>

            <div className="text-xs font-bold text-slate-400">
              {formatDate(comment?.createdAt || comment?.created_at)}
              {comment?.edited ? " · edited" : ""}
            </div>
          </div>
        </div>

        <div className="mt-2 flex min-w-0 flex-wrap items-center gap-2">
          <Pill>{getSubjectLanguage(comment)}</Pill>
          <Pill>{getSubjectAuthor(comment)}</Pill>
          <Pill>{getChapterDisplay(comment)}</Pill>
          <Pill className={getKindStyle(comment)}>{getKindLabel(comment)}</Pill>
        </div>
      </div>

      <div className="shrink-0 rounded-full bg-slate-50 px-3 py-1 text-xs font-black text-slate-500">
        Score {comment?.score || 0}
      </div>
    </div>
  );
}

function SimpleCommentBody({ comment }) {
  const content = comment?.content || comment?.text || comment?.comment || "";

  if (comment?.isDeleted || comment?.deleted) {
    return (
      <div className="mt-4 rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-3 text-sm font-bold italic text-slate-400">
        {comment?.deletedLabel || "Deleted comment"}
      </div>
    );
  }

  return (
    <div className="mt-4 min-w-0 overflow-hidden rounded-2xl border border-slate-200 bg-white px-4 py-4 shadow-sm">
      <p className="whitespace-pre-wrap break-words text-[15px] font-medium leading-7 text-slate-900">
        {content || "No content"}
      </p>
    </div>
  );
}

function EditCommentBox({ comment, onCancel, onSaved }) {
  const [value, setValue] = useState(comment?.content || "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const save = async () => {
    const token = localStorage.getItem("token");
    const id = getCommentId(comment);

    if (!token || !id) return;

    setSaving(true);
    setError("");

    try {
      const res = await fetch(
        `${process.env.REACT_APP_USER_API_URL}/comments/${id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ content: value }),
        }
      );

      if (!res.ok) throw new Error(`Edit ${res.status}`);

      await onSaved?.();
    } catch (err) {
      console.error("edit comment failed:", err);
      setError("Could not save changes.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-4 rounded-2xl border border-blue-200 bg-blue-50/40 p-4">
      <textarea
        value={value}
        onChange={(e) => setValue(e.target.value)}
        className="min-h-[110px] w-full resize-y rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-900 outline-none focus:border-blue-300 focus:ring-4 focus:ring-blue-100"
      />

      {error && (
        <div className="mt-3 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm font-bold text-red-700">
          {error}
        </div>
      )}

      <div className="mt-3 flex flex-wrap justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-black text-slate-600 shadow-sm"
        >
          Cancel
        </button>

        <button
          type="button"
          onClick={save}
          disabled={saving}
          className="rounded-2xl bg-slate-950 px-4 py-2 text-sm font-black text-white shadow-sm disabled:opacity-60"
        >
          {saving ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  );
}
function ReplyComposer({ value, onChange, onCancel, onSubmit, saving }) {
  return (
    <div className="mt-4 rounded-2xl border border-blue-200 bg-blue-50/50 p-4">
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="min-h-[96px] w-full resize-y rounded-2xl border border-blue-200 bg-white px-4 py-3 text-[15px] font-semibold text-slate-900 outline-none focus:border-blue-400 focus:ring-4 focus:ring-blue-100"
        placeholder="Write a reply…"
      />

      <div className="mt-3 flex flex-wrap justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-black text-slate-600 shadow-sm hover:bg-slate-50"
        >
          Cancel
        </button>

        <button
          type="button"
          onClick={onSubmit}
          disabled={saving || !value.trim()}
          className={cx(
            "rounded-2xl px-5 py-2 text-sm font-black text-white shadow-sm",
            saving || !value.trim()
              ? "cursor-not-allowed bg-blue-300"
              : "bg-blue-600 hover:bg-blue-700 shadow-blue-600/20"
          )}
        >
          {saving ? "Posting…" : "Post reply"}
        </button>
      </div>
    </div>
  );
}

function VoteBox({ score = 0, myVote = 0, onVote, disabled = false }) {
  const voteUp = () => {
    if (disabled) return;

    if (myVote === 1) onVote?.(0);
    else if (myVote === -1) onVote?.(0);
    else onVote?.(1);
  };

  const voteDown = () => {
    if (disabled) return;

    if (myVote === -1) onVote?.(0);
    else if (myVote === 1) onVote?.(0);
    else onVote?.(-1);
  };

  return (
    <div className="group relative shrink-0 flex flex-col items-center rounded-2xl border border-slate-200 bg-slate-50 px-2 py-2">
      <button
        type="button"
        disabled={disabled}
        onClick={voteUp}
        className={cx(
          "flex h-8 w-8 items-center justify-center rounded-xl text-base font-black transition",
          disabled
            ? "cursor-not-allowed text-slate-300"
            : myVote === 1
              ? "bg-blue-600 text-white shadow-md shadow-blue-600/20"
              : "text-slate-400 hover:bg-white hover:text-blue-600"
        )}
      >
        ▲
      </button>

      <div className="min-w-9 py-1 text-center text-base font-black text-slate-950">
        {score || 0}
      </div>

      <button
        type="button"
        disabled={disabled}
        onClick={voteDown}
        className={cx(
          "flex h-8 w-8 items-center justify-center rounded-xl text-base font-black transition",
          disabled
            ? "cursor-not-allowed text-slate-300"
            : myVote === -1
              ? "bg-rose-600 text-white shadow-md shadow-rose-600/20"
              : "text-slate-400 hover:bg-white hover:text-rose-600"
        )}
      >
        ▼
      </button>
    </div>
  );
}

function GlobalCommentCard({
  comment,
  auth,
  onRefresh,
  onVote,
  depth = 0,
}) {
  const replies = comment?.replies || [];

  const [editing, setEditing] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [replying, setReplying] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [savingReply, setSavingReply] = useState(false);
  const [replyError, setReplyError] = useState("");

  const commentId = getCommentId(comment);
  const token = localStorage.getItem("token");
  const canInteract = Boolean(token);

  const isDeleted = Boolean(comment?.isDeleted || comment?.deleted);
  const manageable = canManageComment(comment, auth);

  const canReplyHere =
    canInteract &&
    !isDeleted &&
    Boolean(commentId) &&
    Boolean(comment?.subject) &&
    Boolean(comment?.file) &&
    Boolean(comment?.anchor?.text);

  const canEditContent =
    manageable &&
    !isRateMe(comment) &&
    !isRateMeReply(comment) &&
    !isDeleted;

  const alreadyAnsweredByMe =
    isRateMe(comment) && Boolean(comment?.myRateMeReply);

  const payloadBase = {
    subject: comment?.subject,
    file: comment?.file,
    chapterTitle:
      getChapterDisplay(comment) || comment?.chapterTitle || comment?.file,
  };

  const anchor = comment?.anchor?.text
    ? {
        text: comment.anchor.text,
        start: comment.anchor.start,
        end: comment.anchor.end,
      }
    : null;

  const deleteComment = async () => {
    const id = getCommentId(comment);

    if (!token || !id || deleting) return;

    const confirmed = window.confirm(
      "Delete this comment? Replies will stay visible in the thread."
    );

    if (!confirmed) return;

    setDeleting(true);

    try {
      const res = await fetch(
        `${process.env.REACT_APP_USER_API_URL}/comments/${id}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!res.ok) throw new Error(`Delete ${res.status}`);

      await onRefresh?.();
    } catch (err) {
      console.error("delete comment failed:", err);
      alert("Could not delete this comment.");
    } finally {
      setDeleting(false);
    }
  };

  const submitReply = async () => {
    if (!token || !commentId || !replyText.trim() || !anchor?.text) return;

    setSavingReply(true);
    setReplyError("");

    try {
      const res = await fetch(`${process.env.REACT_APP_USER_API_URL}/comments`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          ...payloadBase,
          content: replyText.trim(),
          anchor,
          parentCommentId: commentId,
        }),
      });

      if (!res.ok) throw new Error(`Reply ${res.status}`);

      setReplyText("");
      setReplying(false);

      await onRefresh?.();
    } catch (err) {
      console.error("reply failed:", err);
      setReplyError("Could not post reply.");
    } finally {
      setSavingReply(false);
    }
  };

  return (
    <article
      className={cx(
        "min-w-0 max-w-full overflow-hidden rounded-[28px] border bg-white p-4 shadow-sm transition hover:shadow-md sm:p-5",
        depth > 0
          ? "border-slate-200"
          : isRateMe(comment)
            ? "border-violet-200"
            : "border-slate-200"
      )}
    >
      <div className="flex items-start gap-4">
        {!isDeleted && !isRateMeReply(comment) && (
          <VoteBox
            score={comment?.score || 0}
            myVote={comment?.myVote || 0}
            disabled={!canInteract}
            onVote={(value) => onVote?.(commentId, value)}
          />
        )}

        <div className="min-w-0 flex-1">
          <ThreadHeader comment={comment} />

          {comment?.anchor?.text &&
            !isRateMe(comment) &&
            !isRateMeReply(comment) && (
              <div className="mt-4 min-w-0 overflow-hidden rounded-2xl border border-blue-100 bg-blue-50/60 px-4 py-3">
                <div className="mb-1 text-[10px] font-black uppercase tracking-[0.16em] text-blue-500">
                  Referenced text
                </div>

                <div className="overflow-hidden text-ellipsis whitespace-nowrap text-sm italic leading-6 text-slate-600">
                  “{comment.anchor.text}”
                </div>
              </div>
            )}

          {editing ? (
            <EditCommentBox
              comment={comment}
              onCancel={() => setEditing(false)}
              onSaved={async () => {
                setEditing(false);
                await onRefresh?.();
              }}
            />
          ) : isRateMe(comment) && !isDeleted ? (
            <RateMeCommentBlock
              comment={comment}
              content={comment?.content || ""}
              canInteract={canInteract}
              payloadBase={payloadBase}
              anchor={anchor}
              onSubmitted={onRefresh}
              alreadyAnswered={alreadyAnsweredByMe}
            />
          ) : isRateMeReply(comment) && !isDeleted ? (
            <RateMeReplyBlock reply={comment} />
          ) : (
            <SimpleCommentBody comment={comment} />
          )}

          <div className="mt-4 flex flex-wrap items-center gap-3">
            {canReplyHere ? (
              <button
                type="button"
                onClick={() => {
                  setReplying((value) => !value);
                  setReplyError("");
                }}
                className="text-sm font-black text-blue-600 hover:text-blue-800"
              >
                Reply
              </button>
            ) : !canInteract && !isDeleted ? (
              <button
                type="button"
                disabled
                className="cursor-not-allowed text-sm font-black text-slate-300"
                title="Log in to reply"
              >
                Reply
              </button>
            ) : null}

            {canEditContent && !editing && (
              <button
                type="button"
                onClick={() => setEditing(true)}
                className="text-sm font-black text-slate-600 hover:text-slate-950"
              >
                Edit
              </button>
            )}

            {manageable && !editing && !isDeleted && (
              <button
                type="button"
                onClick={deleteComment}
                disabled={deleting}
                className="text-sm font-black text-slate-700 hover:text-red-600 disabled:opacity-60"
              >
                {deleting ? "Deleting…" : "Delete"}
              </button>
            )}

            <span className="text-xs font-bold text-slate-300">
              {replies.length} repl{replies.length === 1 ? "y" : "ies"}
            </span>
          </div>

          {replyError && (
            <div className="mt-3 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm font-bold text-red-700">
              {replyError}
            </div>
          )}

          {replying && canReplyHere && (
            <ReplyComposer
              value={replyText}
              onChange={setReplyText}
              onCancel={() => {
                setReplying(false);
                setReplyText("");
                setReplyError("");
              }}
              onSubmit={submitReply}
              saving={savingReply}
            />
          )}
        </div>
      </div>

      {replies.length > 0 && (
        <div className="mt-5 min-w-0 space-y-3 border-l-2 border-slate-100 pl-2 sm:pl-4">
          {replies.map((reply, index) => (
            <GlobalCommentCard
              key={getCommentId(reply) || index}
              comment={reply}
              auth={auth}
              onRefresh={onRefresh}
              onVote={onVote}
              depth={depth + 1}
            />
          ))}
        </div>
      )}
    </article>
  );
}

function SubjectPreview({ subject }) {
  const latest = subject?.latest;

  return (
    <div className="mt-3 rounded-2xl border border-slate-100 bg-slate-50/70 px-4 py-3">
      <div className="flex flex-wrap items-center gap-2">
        <MetricPill label="threads" value={subject?.threadCount || 0} tone="blue" />

        {subject?.rateMeCount > 0 && (
          <MetricPill label="Rate me" value={subject.rateMeCount} tone="violet" />
        )}

        {subject?.discussionCount > 0 && (
          <MetricPill
            label="discussion"
            value={subject.discussionCount}
            tone="green"
          />
        )}

        {subject?.replyCount > 0 && (
          <MetricPill label="replies" value={subject.replyCount} />
        )}
      </div>

      {latest && (
        <div className="mt-3 min-w-0 text-sm">
          <span className="font-black text-slate-700">
            Latest: {latest.username || "User"}
          </span>
          <span className="mx-2 text-slate-300">·</span>
          <span className="font-bold text-slate-400">
            {formatDate(latest.updatedAt || latest.createdAt)}
          </span>

          <div className="mt-1 overflow-hidden text-ellipsis whitespace-nowrap font-semibold text-slate-500">
            {getSnippet(latest)}
          </div>
        </div>
      )}
    </div>
  );
}

function SubjectSection({
  subject,
  auth,
  filters,
  forceOpenToken,
  forceClosedToken,
}) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [pagination, setPagination] = useState(null);
  const [threadSort, setThreadSort] = useState("active");
  const [loading, setLoading] = useState(false);
  const [loadedOnce, setLoadedOnce] = useState(false);
  const [error, setError] = useState("");

  const fetchSubjectThreads = async () => {
    if (!subject?.subjectKey) return;

    setLoading(true);
    setError("");

    try {
      const token = localStorage.getItem("token");

      const params = new URLSearchParams({
        page: String(page),
        pageSize: String(pageSize),
        sort: threadSort,
        subjectKey: subject.subjectKey,
        language: filters.language,
        author: filters.author,
        kind: filters.kind,
      });

      const res = await fetch(
        `${process.env.REACT_APP_USER_API_URL}/comments/community/subject?${params.toString()}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({}),
        }
      );

      if (!res.ok) throw new Error(`Subject comments ${res.status}`);

      const json = await res.json();

      setItems(json.items || []);
      setPagination(json);
      setLoadedOnce(true);
    } catch (err) {
      console.error("fetch subject comments failed:", err);
      setError("Could not load this subject.");
    } finally {
      setLoading(false);
    }
  };

  const voteComment = async (commentId, nextVote) => {
    const token = localStorage.getItem("token");

    if (!token || !commentId) return;

    setError("");

    try {
      const res = await fetch(
        `${process.env.REACT_APP_USER_API_URL}/comments/${commentId}/vote`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ value: nextVote }),
        }
      );

      if (!res.ok) throw new Error(`Vote ${res.status}`);

      await fetchSubjectThreads();
    } catch (err) {
      console.error("vote comment failed:", err);
      setError("Could not save vote.");
    }
  };

  useEffect(() => {
    if (forceOpenToken > 0) setOpen(true);
  }, [forceOpenToken]);

  useEffect(() => {
    if (forceClosedToken > 0) setOpen(false);
  }, [forceClosedToken]);

  useEffect(() => {
    setItems([]);
    setPagination(null);
    setLoadedOnce(false);
    setPage(1);
  }, [
    subject?.subjectKey,
    filters.language,
    filters.author,
    filters.kind,
    threadSort,
  ]);

  useEffect(() => {
    if (open) {
      fetchSubjectThreads();
    }
  }, [
    open,
    page,
    pageSize,
    subject?.subjectKey,
    filters.language,
    filters.author,
    filters.kind,
    threadSort,
  ]);

  const refresh = async () => {
    await fetchSubjectThreads();
  };

  return (
    <section
      className={cx(
        "min-w-0 overflow-hidden rounded-[30px] border bg-white shadow-sm transition",
        open
          ? "border-blue-300 bg-blue-50/30 ring-2 ring-blue-100"
          : "border-slate-200"
      )}
    >
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className={cx(
          "flex w-full min-w-0 items-start justify-between gap-4 p-4 text-left transition sm:p-5",
          open ? "bg-blue-50/70" : "hover:bg-slate-50/70"
        )}
      >
        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <h3 className="min-w-0 truncate text-xl font-black text-slate-950">
              {subject.subjectTitle}
            </h3>

            <Pill>{subject.subjectAuthor}</Pill>
            <Pill>{subject.language}</Pill>

            {open && (
              <Pill className="border-blue-200 bg-blue-100 text-blue-700">
                Open
              </Pill>
            )}
          </div>

          <SubjectPreview subject={subject} />
        </div>

        <Chevron open={open} />
      </button>

      {open && (
        <div className="min-w-0 border-t border-blue-100 bg-blue-50/30 p-3 sm:p-5">
          <div className="mb-4 flex min-w-0 flex-col gap-3 rounded-3xl border border-blue-100 bg-white/80 p-4 shadow-sm sm:flex-row sm:items-end sm:justify-between">
            <div className="min-w-0">
              <div className="text-[10px] font-black uppercase tracking-[0.16em] text-blue-400">
                Subject controls
              </div>

              <div className="mt-1 text-sm font-bold text-slate-600">
                Ordering and pagination apply only inside this subject.
              </div>
            </div>

            <label className="flex w-full min-w-0 flex-col gap-1 sm:w-[220px]">
              <span className="text-[10px] font-black uppercase tracking-[0.16em] text-slate-400">
                Thread order
              </span>

              <select
                value={threadSort}
                onChange={(e) => {
                  setThreadSort(e.target.value);
                  setPage(1);
                }}
                className="h-11 min-w-0 rounded-2xl border border-slate-200 bg-white px-3 text-sm font-bold text-slate-700 shadow-sm outline-none transition focus:border-blue-300 focus:ring-4 focus:ring-blue-100"
              >
                <option value="active">Recently active</option>
                <option value="newest">Newest</option>
                <option value="oldest">Oldest</option>
                <option value="ranking">Top ranked</option>
              </select>
            </label>
          </div>

          {error && (
            <div className="mb-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-bold text-red-700">
              {error}
            </div>
          )}

          {loading && !loadedOnce ? (
            <div className="rounded-3xl border border-dashed border-slate-300 bg-white/70 px-6 py-12 text-center text-sm font-bold text-slate-500">
              Loading subject comments…
            </div>
          ) : items.length === 0 ? (
            <div className="rounded-3xl border border-dashed border-slate-300 bg-white/70 px-6 py-12 text-center text-sm font-bold text-slate-500">
              No comments in this subject.
            </div>
          ) : (
            <div className="min-w-0 space-y-4">
              {items.map((comment, index) => (
                <GlobalCommentCard
                  key={getCommentId(comment) || index}
                  comment={comment}
                  auth={auth}
                  onRefresh={refresh}
                  onVote={voteComment}
                />
              ))}
            </div>
          )}

          <Pagination
            compact
            page={page}
            totalPages={pagination?.totalPages || 1}
            setPage={setPage}
          />
        </div>
      )}
    </section>
  );
}

function LanguagePreview({ language }) {
  const latest = language?.latest;

  return (
    <div className="mt-3 flex min-w-0 flex-wrap items-center gap-2">
      <MetricPill label="subjects" value={language?.subjects?.length || 0} />
      <MetricPill label="threads" value={language?.threadCount || 0} tone="blue" />

      {language?.rateMeCount > 0 && (
        <MetricPill label="Rate me" value={language.rateMeCount} tone="violet" />
      )}

      {language?.replyCount > 0 && (
        <MetricPill label="replies" value={language.replyCount} />
      )}

      {latest && (
        <span className="inline-flex min-w-0 max-w-full rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-bold text-slate-500">
          <span className="shrink-0 text-slate-400">Latest&nbsp;</span>
          <span className="truncate">
            {latest.username || "User"} ·{" "}
            {formatDate(latest.updatedAt || latest.createdAt)}
          </span>
        </span>
      )}
    </div>
  );
}

function LanguageSection({
  language,
  auth,
  filters,
  forceOpenToken,
  forceClosedToken,
}) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (forceOpenToken > 0) setOpen(true);
  }, [forceOpenToken]);

  useEffect(() => {
    if (forceClosedToken > 0) setOpen(false);
  }, [forceClosedToken]);

  return (
    <section
      className={cx(
        "min-w-0 overflow-hidden rounded-[36px] border bg-white/80 shadow-sm transition",
        open ? "border-blue-300 ring-2 ring-blue-100" : "border-slate-200"
      )}
    >
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className={cx(
          "flex w-full min-w-0 items-start justify-between gap-4 p-5 text-left transition sm:p-6",
          open
            ? "bg-gradient-to-br from-blue-50 via-white to-blue-50"
            : "bg-gradient-to-br from-white via-slate-50 to-blue-50/40 hover:from-slate-50 hover:to-blue-50"
        )}
      >
        <div className="min-w-0 flex-1">
          <div className="text-[11px] font-black uppercase tracking-[0.18em] text-slate-400">
            Language
          </div>

          <h2 className="mt-1 truncate text-2xl font-black text-slate-950">
            {language.language}
          </h2>

          <LanguagePreview language={language} />
        </div>

        <Chevron open={open} />
      </button>

      {open && (
        <div className="min-w-0 space-y-4 border-t border-blue-100 bg-blue-50/20 p-3 sm:p-5">
          {(language.subjects || []).map((subject) => (
            <SubjectSection
              key={subject.subjectKey || subject.subjectTitle}
              subject={subject}
              auth={auth}
              filters={filters}
              forceOpenToken={forceOpenToken}
              forceClosedToken={forceClosedToken}
            />
          ))}
        </div>
      )}
    </section>
  );
}

export default function CommunityCommentsBrowser({ auth, successHandler }) {
  const [summary, setSummary] = useState({
    languages: [],
    totalThreads: 0,
  });

  const [facets, setFacets] = useState({
    languages: [],
    authors: [],
    subjects: [],
    kinds: [],
  });

  const [language, setLanguage] = useState("all");
  const [author, setAuthor] = useState("all");
  const [subjectKey, setSubjectKey] = useState("all");
  const [kind, setKind] = useState("all");
  const [summarySort, setSummarySort] = useState("active");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [forceOpenToken, setForceOpenToken] = useState(0);
  const [forceClosedToken, setForceClosedToken] = useState(0);

  const subjectOptions = useMemo(() => {
    return (facets.subjects || []).map((subject) => ({
      value: subject.key,
      label: makeSubjectLabel(subject),
    }));
  }, [facets.subjects]);

  const kindOptions = useMemo(() => {
    return (facets.kinds || []).map((item) => ({
      value: item,
      label: getKindLabel(item),
    }));
  }, [facets.kinds]);

  const selectedSubject = useMemo(() => {
    return (facets.subjects || []).find((item) => item.key === subjectKey);
  }, [facets.subjects, subjectKey]);

  const filters = useMemo(
    () => ({
      language,
      author,
      subjectKey,
      kind,
    }),
    [language, author, subjectKey, kind]
  );

  const selectedFilterChips = [
    language !== "all"
      ? {
          label: language,
          clear: () => setLanguage("all"),
        }
      : null,
    author !== "all"
      ? {
          label: author,
          clear: () => setAuthor("all"),
        }
      : null,
    subjectKey !== "all"
      ? {
          label: selectedSubject
            ? makeSubjectLabel(selectedSubject)
            : "Selected subject",
          clear: () => setSubjectKey("all"),
        }
      : null,
    kind !== "all"
      ? {
          label: getKindLabel(kind),
          clear: () => setKind("all"),
        }
      : null,
  ].filter(Boolean);

  const fetchSummary = async () => {
    setLoading(true);
    setError("");

    try {
      const token = localStorage.getItem("token");

      const params = new URLSearchParams({
        language,
        author,
        subjectKey,
        kind,
        sort: summarySort,
      });

      const res = await fetch(
        `${process.env.REACT_APP_USER_API_URL}/comments/community/summary?${params.toString()}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({}),
        }
      );

      if (!res.ok) throw new Error(`Community summary ${res.status}`);

      const json = await res.json();

      const nextFacets = json.facets || {
        languages: [],
        authors: [],
        subjects: [],
        kinds: [],
      };

      setSummary({
        languages: json.languages || [],
        totalThreads: json.totalThreads || 0,
        sort: json.sort,
      });

      setFacets(nextFacets);

      if (
        language !== "all" &&
        !(nextFacets.languages || []).includes(language)
      ) {
        setLanguage("all");
      }

      if (author !== "all" && !(nextFacets.authors || []).includes(author)) {
        setAuthor("all");
      }

      if (
        subjectKey !== "all" &&
        !(nextFacets.subjects || []).some((item) => item.key === subjectKey)
      ) {
        setSubjectKey("all");
      }

      if (kind !== "all" && !(nextFacets.kinds || []).includes(kind)) {
        setKind("all");
      }
    } catch (err) {
      console.error("fetch community summary failed:", err);
      setError("Could not load community summary.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSummary();
  }, [language, author, subjectKey, kind, summarySort]);

  const resetFilters = () => {
    setLanguage("all");
    setAuthor("all");
    setSubjectKey("all");
    setKind("all");
    setSummarySort("active");
    setForceClosedToken((value) => value + 1);
  };

  return (
    <div className="min-h-screen min-w-0 overflow-x-hidden bg-gradient-to-br from-slate-50 via-white to-blue-50/40 px-3 py-5 sm:px-6 lg:px-8">
      <div className="mx-auto min-w-0 max-w-7xl">
        <header className="mb-6 min-w-0 overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm sm:rounded-[36px]">
          <div className="relative border-b border-slate-100 bg-gradient-to-br from-white via-blue-50/50 to-violet-50/40 p-5 sm:p-8">
            <div className="absolute -right-20 -top-20 h-52 w-52 rounded-full bg-blue-300/20 blur-3xl" />
            <div className="absolute -bottom-24 -left-24 h-52 w-52 rounded-full bg-violet-300/20 blur-3xl" />

            <div className="relative flex min-w-0 flex-col gap-5 sm:flex-row sm:items-start sm:justify-between">
              <div className="min-w-0">
                <button
                  type="button"
                  onClick={() => successHandler?.(false)}
                  className="mb-5 inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-black text-slate-700 shadow-sm transition hover:-translate-y-0.5 hover:bg-slate-50"
                >
                  <span>←</span>
                  <span>Back to library</span>
                </button>

                <div className="text-xs font-black uppercase tracking-[0.18em] text-slate-400">
                  Community
                </div>

                <h1 className="mt-1 text-3xl font-black tracking-tight text-slate-950 sm:text-4xl">
                  Community comments
                </h1>

                <p className="mt-3 max-w-3xl text-sm font-semibold leading-6 text-slate-500">
                  Browse by language and subject. Filters narrow each other, and
                  each subject has independent pagination and ordering.
                </p>
              </div>

              <div className="w-full rounded-[28px] bg-slate-950 px-6 py-5 text-white shadow-xl shadow-slate-950/10 sm:w-auto">
                <div className="text-[10px] font-black uppercase tracking-[0.16em] text-white/50">
                  Threads
                </div>

                <div className="mt-1 text-4xl font-black">
                  {summary.totalThreads || 0}
                </div>
              </div>
            </div>
          </div>

          <div className="p-4 sm:p-6">
            <div className="grid min-w-0 grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-5">
              <SelectFilter
                label="Language"
                value={language}
                onChange={setLanguage}
                options={facets.languages}
              />

              <SelectFilter
                label="Author"
                value={author}
                onChange={setAuthor}
                options={facets.authors}
              />

              <SelectFilter
                label="Subject"
                value={subjectKey}
                onChange={setSubjectKey}
                options={subjectOptions}
              />

              <SelectFilter
                label="Type"
                value={kind}
                onChange={setKind}
                options={kindOptions}
              />

              <label className="flex min-w-0 flex-1 flex-col gap-1">
                <span className="text-[10px] font-black uppercase tracking-[0.16em] text-slate-400">
                  Summary order
                </span>

                <select
                  value={summarySort}
                  onChange={(e) => setSummarySort(e.target.value)}
                  className="h-11 min-w-0 rounded-2xl border border-slate-200 bg-white px-3 text-sm font-bold text-slate-700 shadow-sm outline-none transition focus:border-blue-300 focus:ring-4 focus:ring-blue-100"
                >
                  <option value="active">Recently active</option>
                  <option value="newest">Newest</option>
                  <option value="oldest">Oldest</option>
                  <option value="ranking">Top ranked</option>
                </select>
              </label>
            </div>

            <div className="mt-4 flex min-w-0 flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex min-w-0 flex-wrap items-center gap-2 text-sm font-bold text-slate-400">
                <span>
                  {loading
                    ? "Loading…"
                    : `${summary.totalThreads || 0} thread${
                        summary.totalThreads === 1 ? "" : "s"
                      } found`}
                </span>

                {selectedFilterChips.length > 0 && (
                  <div className="flex min-w-0 flex-wrap gap-2">
                    {selectedFilterChips.map((chip) => (
                      <Pill
                        key={chip.label}
                        onRemove={chip.clear}
                        className="border-blue-200 bg-blue-50 text-blue-700"
                      >
                        {chip.label}
                      </Pill>
                    ))}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 gap-2 sm:flex sm:flex-wrap">
                <button
                  type="button"
                  onClick={() => setForceOpenToken((value) => value + 1)}
                  className="rounded-2xl border border-blue-200 bg-blue-50 px-4 py-2 text-sm font-black text-blue-700 shadow-sm transition hover:bg-blue-100"
                >
                  Expand all
                </button>

                <button
                  type="button"
                  onClick={() => setForceClosedToken((value) => value + 1)}
                  className="rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-black text-slate-600 shadow-sm transition hover:bg-slate-50 hover:text-slate-950"
                >
                  Collapse all
                </button>

                <button
                  type="button"
                  onClick={resetFilters}
                  className="rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-black text-slate-600 shadow-sm transition hover:bg-slate-50 hover:text-slate-950"
                >
                  Reset filters
                </button>
              </div>
            </div>
          </div>
        </header>

        {error && (
          <div className="mb-5 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-bold text-red-700">
            {error}
          </div>
        )}

        {loading && summary.languages.length === 0 ? (
          <div className="rounded-[32px] border border-dashed border-slate-300 bg-white/70 px-6 py-20 text-center">
            <div className="text-base font-bold text-slate-500">
              Loading community comments…
            </div>
          </div>
        ) : summary.languages.length === 0 ? (
          <div className="rounded-[32px] border border-dashed border-slate-300 bg-white/70 px-6 py-20 text-center">
            <div className="text-base font-bold text-slate-500">
              No comments found.
            </div>

            <button
              type="button"
              onClick={resetFilters}
              className="mt-5 rounded-2xl bg-slate-950 px-5 py-2.5 text-sm font-black text-white shadow-sm"
            >
              Clear filters
            </button>
          </div>
        ) : (
          <div className="min-w-0 space-y-6">
            {summary.languages.map((languageItem) => (
              <LanguageSection
                key={languageItem.language}
                language={languageItem}
                auth={auth}
                filters={filters}
                forceOpenToken={forceOpenToken}
                forceClosedToken={forceClosedToken}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}